import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';

/**
 * 활성 벤더 목록 (D-1 펀딩 벤더 picker 정본).
 * be-settle 동결 계약(2026-05-31):
 *   GET /admin/v1/vendors?page=&size= → data.items[].{vendorId,vendorName,status,approvedAt,createdAt}
 *   + meta.pagination. **page 1-based**(BO 훅 표준, 컨트롤러 0→1-based 정정됨).
 * 기존 vendor-applications(KYC 신청, applicationId)와 다른 엔드포인트 —
 * 펀딩 생성이 요구하는 실제 vendorId 소스. (스펙 미생성이라 raw fetch + defensive 파싱.)
 */
export interface VendorOption {
  vendorId: string;
  vendorName: string;
  status?: string;
  approvedAt?: string | null;
  createdAt?: string;
}

interface VendorsEnvelope {
  success: boolean;
  data?:
    | { items?: VendorOption[]; content?: VendorOption[] }
    | VendorOption[]
    | null;
}

const extractItems = (data: VendorsEnvelope['data']): VendorOption[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.items ?? data.content ?? [];
};

export interface VendorsQueryParams {
  keyword?: string | undefined;
  size?: number;
}

export const vendorsQueryKey = (params: VendorsQueryParams) =>
  ['admin', 'vendors', params] as const;

export const useVendorsQuery = (params: VendorsQueryParams = {}) =>
  useQuery<VendorOption[]>({
    queryKey: vendorsQueryKey(params),
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.keyword && params.keyword.trim().length > 0) {
        qs.set('keyword', params.keyword.trim());
      }
      // 계약: page 1-based. picker 는 첫 페이지 상위 N건만 필요.
      qs.set('page', '1');
      qs.set('size', String(params.size ?? 20));
      const res = await adminFetch(`/admin/v1/vendors?${qs.toString()}`);
      if (!res.ok) throw new Error(`벤더 목록을 불러오지 못했습니다. (${res.status})`);
      const env = (await res.json()) as VendorsEnvelope;
      return extractItems(env.data);
    },
    staleTime: 60 * 1000,
  });
